/**
 * The pure half of Administración → Contactos (contract v10.44, ADR 0038 of the back): a
 * group's addresses as one text, one per line, and back. Out of the component so the tests
 * pin what is sent without rendering.
 */
import {
  type Contact,
  type ContactChange,
  type GroupMember,
  MAX_GROUP_MEMBERS,
} from '@/shared/contacts/types';
import { recipientPieces } from '@/shared/emails/sending';

const ADDRESS = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;

/** The members as the field shows them: «Ana Pérez <ana@x.es>» or the address, one a line. */
export function membersText(members: readonly { address: string; name?: string | null }[]): string {
  return members
    .map((member) => (member.name ? `${member.name} <${member.address}>` : member.address))
    .join('\n');
}

/**
 * What was typed in a group's field, as the API takes it: one address a line (commas and
 * semicolons work too, for a list pasted from the mail client), «Nombre <dirección>» with
 * its name, each address once whatever its case. A comma inside a name does not split it
 * (`recipientPieces`): Outlook writes «Pérez García, Ana <ana@x.es>». `problem` says what
 * stops «Guardar».
 */
export function parseMembers(text: string): { members: GroupMember[]; problem: string | null } {
  const members: GroupMember[] = [];
  const seen = new Set<string>();
  for (const line of recipientPieces(text)) {
    const bracketed = /^(.*?)<([^>]*)>\s*$/.exec(line);
    const address = (bracketed ? (bracketed[2] ?? '') : line).trim();
    const name = bracketed
      ? (bracketed[1] ?? '')
          .trim()
          .replace(/^"(.*)"$/, '$1')
          .trim()
      : '';
    if (!ADDRESS.test(address)) {
      return { members, problem: `«${line}» no es una dirección de correo.` };
    }
    const key = address.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    members.push(name ? { address, name } : { address });
  }
  if (members.length > MAX_GROUP_MEMBERS) {
    return {
      members,
      problem: `Como mucho ${MAX_GROUP_MEMBERS} direcciones por grupo (hay ${members.length}).`,
    };
  }
  return { members, problem: null };
}

/** «Corregir» a contact: only what changed travels (the API keeps the rest). */
export function contactChanges(
  contact: Pick<Contact, 'address' | 'name'>,
  form: { address: string; name: string },
): ContactChange {
  const change: ContactChange = {};
  const address = form.address.trim();
  if (address && address.toLowerCase() !== contact.address.toLowerCase()) change.address = address;
  const name = form.name.trim().split(/\s+/).filter(Boolean).join(' ');
  if (name !== (contact.name ?? '')) change.name = name || null;
  return change;
}

/** «12 correos», «1 correo»: what a contact's uses mean on screen. */
export function usesText(uses: number): string {
  return `${uses} ${uses === 1 ? 'correo' : 'correos'}`;
}
